import React, {PropsWithChildren } from "react";
import {
    FieldName,
    FieldValues,
    useController,
    UseFormMethods,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DatePicker } from "components";
import cn from "classnames";

// Importing only the picker stylesheet because the default way of doing this
// imports ant's whole global css, which messes up our styles.
import "antd/lib/date-picker/style/index.css";
import { RangeDateRender } from 'rc-picker/lib/RangePicker';
import "./DateTimeRange.scss";
import { applyTimezone, isValidDate, unapplyTimezone } from "services/Utils";
import { DateTime } from "luxon";

interface IDateTimeRangeProps<FormValues> {
    allowClear?: boolean,
    className?: string,
    dateRender?: RangeDateRender<Date>,
    defaultDateFormat?: string,
    disabled?: boolean,
    end: Date,
    form: UseFormMethods<FormValues>,
    label: string,
    // Fancy Typing Alert:
    // This makes the caller pass in the form property names.
    // The "keyof FormValues" forces this property to be a key of the form object that's passed in.
    // The "FieldName<FormValues>" is used internally tell "useController" that this property is on the form.
    // We're using "&" to combine them to make both things happy.
    nameEndDate: keyof FormValues & FieldName<FormValues>,
    nameEndTime: keyof FormValues & FieldName<FormValues>,
    nameStartDate: keyof FormValues & FieldName<FormValues>,
    nameStartTime: keyof FormValues & FieldName<FormValues>,
    required?: boolean,
    start: Date,
    timezone?: string,
    onChange: (startDate: Date, endDate: Date) => void,
}

// A combined date and time picker. Expects to be passed an interface describing the form.
export function DateTimeRange<TFormValues extends FieldValues>(props: PropsWithChildren<IDateTimeRangeProps<TFormValues>>) {
    let {
        allowClear = true,
        className,
        dateRender,
        defaultDateFormat,
        disabled,
        end,
        form,
        label,
        nameEndDate,
        nameEndTime,
        nameStartDate,
        nameStartTime,
        required,
        start,
        timezone,
        onChange,
    } = props;

    const { t } = useTranslation();
    const RangePicker = DatePicker.RangePicker;
    const TimePicker = DatePicker.TimePicker;
    const dateFormat = defaultDateFormat || "EEE, MMM d, yyyy";
    const timeFormat = "h:mm a";
    const hasTimezone = timezone && timezone !== 'local';

    const startDt = DateTime.fromJSDate(start, {zone: timezone});
    const endDt = DateTime.fromJSDate(end, {zone: timezone});

    // Ant pickers don't support timezones, so we need to apply them.
    // We'll convert back in the onChange handlers too.
    let startTimezoned = applyTimezone(startDt);
    let endTimezoned = applyTimezone(endDt);

    // If dates aren't valid, the antd picker will blow up, so we need to pass "undefined" instead
    if (!startDt.isValid) startTimezoned = undefined;
    if (!endDt.isValid) endTimezoned = undefined;

    const rules = {
        required: required,
        validate: (v) => {
            return isValidDate(v);
        },
    }

    // Hooking up custom datepicker and timepickers to react-hook-form.
    const startDateCtrl = useController({
        name: nameStartDate,
        control: form.control,
        defaultValue: startTimezoned || null,
        rules: rules,
    });
    const endDateCtrl = useController({
        name: nameEndDate,
        control: form.control,
        defaultValue: endTimezoned || null,
        rules: rules,
    });
    const startTimeCtrl = useController({
        name: nameStartTime,
        control: form.control,
        defaultValue: startTimezoned || null,
        rules: rules,
    });
    const endTimeCtrl = useController({
        name: nameEndTime,
        control: form.control,
        defaultValue: endTimezoned || null,
        rules: rules,
    });

    // Show an error if any field is invalid. By relying on the form for validation,
    // we get nice features like how the error won't show until the user has touched the field.
    const hasError = form.errors[nameStartDate]
        || form.errors[nameEndDate]
        || form.errors[nameStartTime]
        || form.errors[nameEndTime];

    // Calls the change handler to propogate start date and end date up to form
    function handleDatesChange(range: [Date, Date]) {
        let newStart = range ? range[0] : null;
        let newEnd = range ? range[1] : null;

        // AntD pickers don't support timezones, so we need to convert from local time.
        // NOTE: We lose the end time hours and minutes, the picker sets them equal to the start time.
        // To work around this, we need to copy over the times BEFORE we unapply the timezone.
        // This is because the times are displayed in the user's selected timezone,
        // so if they've selected "Feb 4" and "5:00pm", we need to combine those before we 
        // unapply the timezone.
        if (newStart) {
            newStart.setHours(startTimezoned.getHours(), startTimezoned.getMinutes(), 0, 0);
            newStart = unapplyTimezone(newStart, timezone).toJSDate();
        }
        if (newEnd) {
            newEnd.setHours(endTimezoned.getHours(), endTimezoned.getMinutes(), 0, 0);
            newEnd = unapplyTimezone(newEnd, timezone).toJSDate();
        }

        // Tell the form that our custom datepicker changed. This makes the form do its validation
        // and set the values on the form object.
        startDateCtrl.field.onChange(newStart);
        startTimeCtrl.field.onChange(newStart);
        endDateCtrl.field.onChange(newEnd);
        endTimeCtrl.field.onChange(newEnd);

        // Also tell our parent that the dates changed.
        onChange(newStart, newEnd);
    };

    function handleStartTimeChange(newStart: Date) {
        if (newStart) {
            // We only support selecting minutes, so clear the seconds and milliseconds to be standard.
            newStart.setSeconds(0, 0);
            newStart = unapplyTimezone(newStart, timezone).toJSDate();
        }

        startDateCtrl.field.onChange(newStart);
        startTimeCtrl.field.onChange(newStart);
        onChange(newStart, end);
    }

    function handleEndTimeChange(newEnd: Date) {
        if (newEnd) {
            newEnd.setSeconds(0, 0);
            newEnd = unapplyTimezone(newEnd, timezone).toJSDate();
        }

        endDateCtrl.field.onChange(newEnd);
        endTimeCtrl.field.onChange(newEnd);
        onChange(start, newEnd);
    }

    return (
        <div
            className={cn(className, {
                formItem: true,
                error: hasError,
            })}
        >
            <div className="formItem_label">{label}</div>
            <RangePicker
                getPopupContainer={(trig) => trig.parentNode as HTMLElement}
                allowClear={allowClear}
                className="dateTimeRange dateRange"
                dateRender={dateRender}
                value={[startTimezoned, endTimezoned]}
                format={dateFormat}
                onChange={handleDatesChange}
                placeholder={["Start Date", "End Date"]}
                inputReadOnly={true}
                disabled={disabled}
                // We don't need to pass the endDate ref because the fields are set together,
                // so if 1 is invalid, both are invalid.
                ref={startDateCtrl.field.ref}
            />
            <div className="timepicker">
                <div className="dateTimeRange_connector"></div>
                <TimePicker
                    getPopupContainer={(trig) => trig.parentNode as HTMLElement}
                    className="dateTimeRange timeRange"
                    value={startTimezoned}
                    format={timeFormat}
                    showNow={allowClear}
                    placeholder="Start Time"
                     // We're not allowing clearing these because it doesn't work currently,
                     // and we don't really need to allow it.
                    allowClear={false}
                    inputReadOnly={true}
                    disabled={disabled}
                    onSelect={handleStartTimeChange}
                    ref={startTimeCtrl.field.ref}
                />
                {hasTimezone && (
                    <span className="timepicker_timezone">
                        {timezone}
                    </span>
                )}
            </div>
            <div className="timepicker">
                <div className="dateTimeRange_connector"></div>
                <TimePicker
                    getPopupContainer={(trig) => trig.parentNode as HTMLElement}
                    className="dateTimeRange timeRange"
                    value={endTimezoned}
                    format={timeFormat}
                    showNow={false}
                    placeholder="End Time"
                    allowClear={false}
                    inputReadOnly={true}
                    disabled={disabled}
                    onSelect={handleEndTimeChange}
                    ref={endTimeCtrl.field.ref}
                />
                {hasTimezone && (
                    <span className="timepicker_timezone">
                        {timezone}
                    </span>
                )}
            </div>

            {hasError && <div className="formItem_error">
                {t("errors.validation.required")}
            </div>}
        </div>
    );
}
